import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, FileText, CheckSquare, List, MessageSquare, Headphones, GraduationCap, Lock, Download, ShieldCheck, ArrowRight } from "lucide-react";
import { PublicShell } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const TYPE_ICON = { "Book": BookOpen, "Mini-Course": GraduationCap, "Guide": FileText, "Checklist": CheckSquare, "Listicle": List, "Prompt Pack": MessageSquare, "Audio": Headphones };
const inr = (p) => `₹${Number(p).toLocaleString("en-IN")}`;

export default function Library() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [buying, setBuying] = useState(false);
  const { data, isLoading, refetch } = useQuery({ queryKey: ["library"], queryFn: async () => (await api.get("/library")).data });

  const buy = async () => {
    if (!user) { navigate("/login", { state: { returnTo: "/library" } }); return; }
    setBuying(true);
    try {
      const { data: ord } = await api.post("/library/order");
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load checkout");
      const rzp = new window.Razorpay({
        key: ord.key_id, amount: ord.amount, currency: ord.currency, name: ord.name, description: ord.description, order_id: ord.order_id,
        prefill: { name: user.name, email: user.email }, theme: { color: "#059669" },
        handler: async (resp) => {
          try {
            await api.post("/library/verify", null, { params: { order_id: resp.razorpay_order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature } });
            toast.success("Welcome to The Library — every program is unlocked.");
            refetch();
          } catch { toast.error("Payment captured but verification failed — contact support."); }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally { setBuying(false); }
  };

  const download = async (assetId) => {
    try {
      const { data: dl } = await api.get(`/library/assets/${assetId}/download`);
      window.open(dl.url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Not available yet"); }
  };

  const programs = data?.programs || [];
  const owned = !!data?.owned;
  const totalAssets = programs.reduce((n, p) => n + (p.assets?.length || 0), 0);

  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-navy grain">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navysurface/90" />
        <div className="relative mx-auto max-w-4xl px-5 py-20 text-center lg:px-8 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-emerald" data-testid="library-overline">The Library</p>
          <h1 className="mt-5 font-heading text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl" data-testid="library-title">
            Nine complete programs. {totalAssets} resources. One price.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-300" data-testid="library-dek">
            Nine complete programs — {totalAssets} books, guides, checklists, prompt packs and audio sessions on sales, cash flow, hiring, pricing and ecommerce. Curated and rebranded for Indian product businesses.
          </p>
          <div className="mx-auto mt-9 inline-flex flex-col items-center rounded-xl border border-white/10 bg-white/5 px-10 py-7 backdrop-blur" data-testid="library-price-card">
            <p className="font-heading text-4xl font-bold text-white">{inr(data?.price ?? 499)}</p>
            <p className="mt-1 text-sm text-slate-400">Lifetime access · all nine programs · every future addition included</p>
            {owned ? (
              <p className="mt-5 inline-flex items-center gap-2 rounded-md bg-emerald/20 px-5 py-2.5 text-sm font-semibold text-emerald" data-testid="library-owned-badge"><ShieldCheck className="h-4 w-4" /> You own The Library — downloads are unlocked below</p>
            ) : (
              <button onClick={buy} disabled={buying} data-testid="library-buy-btn"
                className="mt-5 rounded-md bg-emerald px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60">
                {buying ? "Opening checkout…" : `Get The Library — ${inr(data?.price ?? 499)}`}
              </button>
            )}
            <p className="mt-3 text-[11px] text-slate-500">Personal use only — these materials are licensed to you, not for resale.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8" data-testid="library-programs">
        {isLoading ? <p className="text-center text-slate-500">Loading…</p> : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {programs.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`library-program-${p.id}`}>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald">{p.category}</p>
                <h3 className="mt-2 font-heading text-lg font-bold text-navy">{p.name}</h3>
                {p.description && <p className="mt-1.5 text-sm text-slate-600">{p.description}</p>}
                <ul className="mt-4 space-y-2">
                  {(p.assets || []).map((a) => {
                    const Icon = TYPE_ICON[a.type] || FileText;
                    return (
                      <li key={a.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`library-asset-${a.id}`}>
                        <span className="flex min-w-0 items-center gap-2 text-slate-700">
                          <Icon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                          <span className="truncate">{a.title}</span>
                        </span>
                        {owned ? (
                          a.has_file ? (
                            <button onClick={() => download(a.id)} data-testid={`library-dl-${a.id}`}
                              className="inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-emerald/10 px-2.5 py-1 text-[11px] font-semibold text-emerald hover:bg-emerald/20"><Download className="h-3 w-3" /> Download</button>
                          ) : (
                            <span className="flex-shrink-0 rounded-md bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-400">File pending</span>
                          )
                        ) : (
                          <Lock className="h-3.5 w-3.5 flex-shrink-0 text-slate-300" />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
        {!owned && !isLoading && (
          <div className="mt-12 text-center">
            <button onClick={buy} disabled={buying} data-testid="library-buy-btn-bottom"
              className="rounded-md bg-emerald px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60">
              {buying ? "Opening checkout…" : `Get all nine programs — ${inr(data?.price ?? 499)}`}
            </button>
            <p className="mt-3 text-xs text-slate-500">Lifetime access · personal use only · not for resale</p>
          </div>
        )}
        <div className="mt-14 rounded-xl border border-slate-200 bg-offwhite p-6 text-center" data-testid="library-course-band">
          <p className="text-[15px] text-slate-700">
            Want the version Shubham teaches live?{" "}
            <button onClick={() => navigate("/sales-mastery")} className="font-semibold text-emerald underline-offset-4 hover:underline">
              Ultimate Sales Mastery is the full course <ArrowRight className="inline h-3.5 w-3.5" />
            </button>
          </p>
        </div>
        <p className="mt-8 text-center text-sm text-slate-500" data-testid="library-stack-link">
          Running the business day to day?{" "}
          <button onClick={() => navigate("/stack")} className="font-semibold text-emerald underline-offset-4 hover:underline">The Stack</button>
          {" "}— the tools Shubham actually uses.
        </p>
      </section>
    </PublicShell>
  );
}
